import axios from "axios";
import { firstRegionalUserPath } from "@/utils/regional-user-permissions";

const axiosInstance = axios.create({
  baseURL: process.env.VUE_APP_URL_API,
});

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch (error) {
    return null;
  }
}

/**
 * Distingue "o servidor respondeu negando" de "não deu para falar com o servidor".
 *
 * POR QUE ISTO EXISTE
 * No navegador, perder a rede é evento raro. Dentro do aplicativo é rotina:
 * elevador, metrô, área sem sinal. Sem essa distinção, uma falha de rede era
 * tratada como sessão inválida e o usuário caía no `/login` — que, neste
 * sistema, chama `logout()` no `created()` e APAGA o token. Resultado prático:
 * o consultor era deslogado por abrir o aplicativo num lugar sem sinal.
 *
 * Um erro de rede do axios chega SEM `error.response` (a requisição nem teve
 * resposta HTTP). Timeout e `ERR_NETWORK` entram no mesmo caso.
 */
export function isNetworkError(error) {
  if (!error) {
    return false;
  }

  if (error.response) {
    return false;
  }

  if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") {
    return true;
  }

  // `error.request` preenchido e `response` vazio = saiu e não voltou.
  return Boolean(error.request);
}

function handleForbiddenAccess(error) {
  const user = getStoredUser();
  const message =
    error.response && error.response.data && error.response.data.message
      ? error.response.data.message
      : "";

  if (user && user.regional_user && message.indexOf("Acesso nao permitido") === 0) {
    const fallbackPath = firstRegionalUserPath(user);
    const nextPath = window.location.pathname === fallbackPath ? "/404" : fallbackPath;

    if (window.location.pathname !== nextPath) {
      window.location.href = nextPath;
    }

    return Promise.reject(error);
  }

  localStorage.clear();

  // O container nativo espelha `token`/`user` em storage do sistema. Sem apagar
  // o espelho, a próxima abertura re-hidrataria o localStorage e "desfaria" o
  // logout. A ponte observa o `localStorage.clear()` e limpa sozinha, mas a
  // chamada explícita torna a intenção visível para quem lê o código.
  if (window.TeamsNative && window.TeamsNative.session) {
    try {
      window.TeamsNative.session.clear();
    } catch (e) {
      /* fora do aplicativo não há espelho para limpar */
    }
  }

  window.location.href = "/login";
  return Promise.reject(error);
}

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["x-auth-token"] = token;
    }
    return config;
  },
  (error) => {
    if (isNetworkError(error)) {
      return Promise.reject(error);
    }
    if (error.response && error.response.status === 403) {
      return handleForbiddenAccess(error);
    }
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Falha de rede NUNCA pode virar logout. Ver o comentário de
    // `isNetworkError()` acima: era assim que o usuário perdia a sessão só por
    // abrir o aplicativo sem sinal.
    if (isNetworkError(error)) {
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 403) {
      const url = error.config && error.config.url ? error.config.url : "";
      const path = window.location.pathname;

      const publicRoutes = [
        "login",
        "apn/cadastro",
        "afiliados/register",
        "nps/resposta",
      ];

      const isPublicApiCall = publicRoutes.some((r) => url.indexOf(r) === 0);
      const isPublicPage = path === "/login/secretaria" || path.indexOf("/apn/cadastro") === 0 || path === "/nps";

      if (isPublicApiCall || isPublicPage) {
        return Promise.reject(error);
      }

      return handleForbiddenAccess(error);
    }
    return Promise.reject(error);
  }
);

export const api = {
  get(endpoint) {
    return axiosInstance.get(endpoint);
  },
  post(endpoint, body) {
    return axiosInstance.post(endpoint, body);
  },
  put(endpoint, body) {
    return axiosInstance.put(endpoint, body);
  },
  delete(endpoint) {
    return axiosInstance.delete(endpoint);
  },
};
